// Iter 104 — Diagnostic drawer that surfaces the raw symbol/side
// bucket breakdown for the currently-audited trades.
//
// Triggered by a button right under the Strengths section. Calls the
// backend `/api/trade-doctor/debug-strengths` endpoint (no LLM call,
// no cache write) and renders:
//   • Raw observed (symbol, side) pairs WITH counts — so the user
//     can spot a single mis-cased symbol that's fragmenting their
//     bucket (e.g. "XAUUSD" / "xauusd").
//   • Canonical normalization sample (raw → normalized).
//   • Full untruncated bucket list (the source of truth for
//     `top_strengths`).
//   • The final `top_strengths` array.
//
// Purpose: when a strengths card looks wrong on a user's real CSV,
// this drawer answers "what does the backend actually see?" in one
// click — instead of needing curl, a token, and a JSON parser.
import React, { useState } from "react";
import { Button } from "../../components/ui/button";
import { ChevronDown, ChevronUp, Microscope } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

export function StrengthsDiagnosticDrawer({ trades, testid = "strengths-diagnostic" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchDiagnostic = async () => {
    if (!trades || trades.length === 0) {
      toast.error("No trades loaded — generate an audit first.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: resp } = await api.post(
        "/trade-doctor/debug-strengths",
        { trades, language: "en" },
      );
      setData(resp);
      setOpen(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Diagnostic failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid={testid}
      className="bt-card p-4 border border-dashed border-[var(--bt-border)]"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Microscope className="w-4 h-4 text-[var(--bt-text-muted)]" />
          <p className="bt-label text-[var(--bt-text-muted)]">
            Strengths Diagnostic (admin)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={fetchDiagnostic}
            disabled={loading}
            data-testid={`${testid}-fetch-btn`}
          >
            {loading ? "Loading…" : data ? "Refresh raw data" : "Show raw bucket data"}
          </Button>
          {data && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setOpen((v) => !v)}
              data-testid={`${testid}-toggle-btn`}
            >
              {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <p className="mt-3 text-sm text-[var(--bt-loss)]" data-testid={`${testid}-error`}>
          {error}
        </p>
      )}

      {open && data && (
        <div className="mt-4 space-y-5 text-sm" data-testid={`${testid}-panel`}>
          {/* TOP_STRENGTHS — the final selection that drives the Strengths cards */}
          <div>
            <p className="bt-label">Top Strengths (final cards)</p>
            {data.top_strengths?.length ? (
              <ul className="mt-2 space-y-1.5" data-testid={`${testid}-top-strengths`}>
                {data.top_strengths.map((s, i) => (
                  <li key={i} className="bt-num text-[var(--bt-text)]">
                    {s.symbol} {s.side} — {s.count} trades, {s.win_rate_pct}% WR, net ${s.net_pnl_usd}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-[var(--bt-text-muted)]">
                Empty — no profitable (symbol, side) bucket with ≥5 trades.
              </p>
            )}
          </div>

          {/* RAW OBSERVED PAIRS — where case / alias fragmentation shows up */}
          <div>
            <p className="bt-label">Raw observed (symbol, side) pairs in your data</p>
            <p className="text-xs text-[var(--bt-text-muted)] mt-0.5">
              If you see the same symbol with multiple case variants here (e.g. <code>{"'XAUUSD'"}</code> and <code>{"'xauusd'"}</code>),
              that&apos;s the fragmentation source. Iter-104 normalisation collapses them at the bucket layer.
            </p>
            <ul
              className="mt-2 space-y-0.5 bt-num text-xs text-[var(--bt-text)] max-h-72 overflow-y-auto"
              data-testid={`${testid}-raw-pairs`}
            >
              {data.raw_observed_pairs?.map((p, i) => (
                <li key={i}>
                  symbol={p.symbol_repr} · side={p.side_repr} · count={p.count}
                </li>
              ))}
            </ul>
          </div>

          {/* NORMALIZATION SAMPLE */}
          <div>
            <p className="bt-label">Normalization sample (raw → canonical)</p>
            <ul
              className="mt-2 space-y-0.5 bt-num text-xs text-[var(--bt-text)] max-h-56 overflow-y-auto"
              data-testid={`${testid}-norm-sample`}
            >
              {data.sample_normalization_per_pair?.map((s, i) => (
                <li key={i}>
                  {JSON.stringify(s.raw_symbol)}/{JSON.stringify(s.raw_side)} → {s.normalized_symbol}/{s.normalized_side}
                </li>
              ))}
            </ul>
          </div>

          {/* FULL UNTRUNCATED BUCKETS */}
          <div>
            <p className="bt-label">Full untruncated bucket list (post-normalization)</p>
            <p className="text-xs text-[var(--bt-text-muted)] mt-0.5">
              Source of truth for <code>top_strengths</code>. Sorted by |net P&amp;L| desc.
            </p>
            <ul
              className="mt-2 space-y-0.5 bt-num text-xs text-[var(--bt-text)] max-h-72 overflow-y-auto"
              data-testid={`${testid}-full-buckets`}
            >
              {data.normalized_buckets_full_untruncated?.map((b, i) => (
                <li key={i}>
                  {b.symbol} {b.side} — {b.count} trades, {b.win_rate_pct}% WR, net ${b.net_pnl_usd}
                </li>
              ))}
            </ul>
          </div>

          <p className="text-[10px] text-[var(--bt-text-muted)]">
            Endpoint: <code>POST /api/trade-doctor/debug-strengths</code> · Total trades
            analysed: <span className="bt-num">{data.total_trades}</span>
          </p>
        </div>
      )}
    </section>
  );
}

export default StrengthsDiagnosticDrawer;
